import React, { useMemo } from "react";

import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { endOfDay, isAfter, isBefore } from "date-fns";
import { useFlags } from "launchdarkly-react-client-sdk";
import { FormProvider, useForm } from "react-hook-form";

import { TaskSource } from "@fartherfinance/frontend/api/Tasks/Types";
import { TasksFilterQueryParamsV4 } from "@fartherfinance/frontend/api/TaskWorkflows/requests/getTasks";
import { Custodian } from "@fartherfinance/frontend/api/Types";

import { TASKS_DEFAULT_FILTERING_QUERY_PARAMS } from "../common";
import { isDateValid } from "../utils";
import Button from "@src/multiCustodian/components/MUI/Button/Button";
import { EnhancedAutocompleteOptionV2 } from "@src/multiCustodian/pages/Advisor/common";
import FlexWrapper from "@src/sharedComponents/Forms/FlexWrapper";
import FormComboboxField from "@src/sharedComponents/Forms/FormComboboxField";
import FormDateField from "@src/sharedComponents/Forms/FormDateField";
import FormH1 from "@src/sharedComponents/Forms/FormH1";
import FormSubHeader from "@src/sharedComponents/Forms/FormSubHeader";
import Modal from "@src/sharedComponents/Modal/Modal";

import {
  allServiceTeamOption,
  noServiceTeamOption,
} from "./TasksFilterForm.const";
import {
  transformFilterQueryParamsToFormValues,
  transformFormValuesToFilterQueryParams,
} from "./TasksFilterForm.utils";
import useTaskResourceOptions from "./useTaskResourceOptions";

import styles from "./TasksFilterForm.module.css";

const isInFuture = (date: Date | null, maxDate: Date): boolean | string => {
  return date && isAfter(date, maxDate) ? "Cannot select future dates" : true;
};

export interface FormData {
  filterByAssignee?: EnhancedAutocompleteOptionV2<string>[];
  filterByClient?: EnhancedAutocompleteOptionV2<string>[];
  filterByAdvisor?: EnhancedAutocompleteOptionV2<string>[];
  filterByTaskStatus?: EnhancedAutocompleteOptionV2<string>[];
  filterByTaskType?: EnhancedAutocompleteOptionV2<string>[];
  filterByTaskPriority?: EnhancedAutocompleteOptionV2<string>[];
  filterByCustodian?: EnhancedAutocompleteOptionV2<Custodian>[];
  filterByTaskSource?: EnhancedAutocompleteOptionV2<TaskSource>[];
  filterByServiceTeam?: EnhancedAutocompleteOptionV2<string | undefined>[];
  filterByCreatedAfter?: Date | null;
  filterByCreatedBefore?: Date | null;
  filterByUpdatedAfter?: Date | null;
  filterByUpdatedBefore?: Date | null;
  filterByDueDateAfter?: Date | null;
  filterByDueDateBefore?: Date | null;
}

interface TasksFilterFormProps {
  currentFilters: TasksFilterQueryParamsV4;
  showTaskStatusPill: boolean;
  onSubmit: (
    newFilters: TasksFilterQueryParamsV4,
    showTaskStatusPill: boolean
  ) => void;
  onClose: () => void;
}

const TasksFilterForm: React.FC<TasksFilterFormProps> = ({
  currentFilters,
  showTaskStatusPill,
  onSubmit,
  onClose,
}) => {
  const { enableTaskServiceTeams } = useFlags();
  const taskResourceOptions = useTaskResourceOptions();

  const formOptions = useMemo(() => {
    return {
      ...taskResourceOptions,
      assigneesOptions: [
        {
          label: "Unassigned",
          value: "null",
          meta: {
            isFixedOption: true,
          },
        },
        ...taskResourceOptions.assigneesOptions,
      ],
      serviceTeamsOptions: [
        allServiceTeamOption,
        ...taskResourceOptions.serviceTeamsOptions,
        noServiceTeamOption,
      ],
    };
  }, [taskResourceOptions]);

  const {
    advisorsOptions,
    assigneesOptions,
    clientsOptions,
    serviceTeamsOptions,
    taskTypesOptions,
    taskStatusesOptions,
    taskPrioritiesOptions,
    custodianOptions,
    generationOptions,
  } = formOptions;

  const form = useForm<FormData>({
    mode: "onChange",
    values: transformFilterQueryParamsToFormValues({
      filters: currentFilters,
      options: formOptions,
    }),
  });

  const maxDate = useMemo(() => endOfDay(new Date()), []);

  const handleFormSubmit = (): void => {
    const { isDirty } = form.getFieldState("filterByTaskStatus");
    const shouldShowTaskStatusPill = isDirty || showTaskStatusPill;

    form.handleSubmit((formData) => {
      onSubmit(
        transformFormValuesToFilterQueryParams(formData),
        shouldShowTaskStatusPill
      );
      onClose();
    })();
  };

  const handleFormReset = (): void => {
    // NOTE: this operation resets form to the global default state
    const transformedFormValues = transformFilterQueryParamsToFormValues({
      filters: TASKS_DEFAULT_FILTERING_QUERY_PARAMS,
      options: formOptions,
    });

    const newFormValues = {
      ...transformedFormValues,
      filterByTaskType: [],
      filterByAssignee: [],
      filterByAdvisor: [],
      filterByClient: [],
      filterByCustodian: [],
      filterByTaskSource: [],
      filterByCreatedAfter: null,
      filterByCreatedBefore: null,
      filterByUpdatedAfter: null,
      filterByUpdatedBefore: null,
      filterByDueDateAfter: null,
      filterByDueDateBefore: null,
      filterByServiceTeam: [allServiceTeamOption],
    };

    Object.entries(newFormValues).forEach(([key, value]) => {
      form.setValue(key as keyof FormData, value);
    });
  };

  return (
    <Modal closeModal={onClose}>
      <FormH1>Filter Tasks</FormH1>
      <FormProvider {...form}>
        <form
          className={styles.form}
          onSubmit={form.handleSubmit(handleFormSubmit)}
        >
          <FlexWrapper>
            <FormComboboxField
              name="filterByAssignee"
              label="Assignee"
              values={assigneesOptions}
              preserveSearchOnSelect
            />

            <FormComboboxField
              name="filterByAdvisor"
              label="Advisor"
              values={advisorsOptions}
              preserveSearchOnSelect
            />

            <FormComboboxField
              name="filterByClient"
              label="Client"
              values={clientsOptions}
              preserveSearchOnSelect
            />
            {enableTaskServiceTeams ? (
              <FormComboboxField
                name="filterByServiceTeam"
                label="Service Team"
                values={serviceTeamsOptions}
                clearOthersOnUndefined
              />
            ) : (
              <FormComboboxField
                name="filterByTaskSource"
                label="Generation Type"
                values={generationOptions}
                preserveSearchOnSelect
              />
            )}
          </FlexWrapper>

          <FlexWrapper className={styles.flexWrapperMultiSelectRow}>
            <FormComboboxField
              name="filterByTaskStatus"
              label="Status"
              values={taskStatusesOptions}
              preserveSearchOnSelect
            />
            <FormComboboxField
              name="filterByTaskPriority"
              label="Priority"
              values={taskPrioritiesOptions}
              preserveSearchOnSelect
            />
            <FormComboboxField
              name="filterByTaskType"
              label="Type"
              values={taskTypesOptions}
              preserveSearchOnSelect
            />
            <FormComboboxField
              name="filterByCustodian"
              label="Custodian"
              values={custodianOptions}
              preserveSearchOnSelect
            />

            {enableTaskServiceTeams && (
              <FormComboboxField
                name="filterByTaskSource"
                label="Generation Type"
                values={generationOptions}
                preserveSearchOnSelect
              />
            )}
          </FlexWrapper>

          <FormSubHeader className={styles.subHeader}>
            Date Created
          </FormSubHeader>
          <FlexWrapper className={styles.flexWrapper}>
            <FormDateField
              name="filterByCreatedAfter"
              label="From"
              placeholder="Select a date"
              maxDate={maxDate}
              rules={{
                deps: ["filterByCreatedBefore"],
                validate: {
                  isDateValid,
                  isInFuture: (value) => isInFuture(value, maxDate),
                  isInRange: (value, { filterByCreatedBefore }) => {
                    return value && isAfter(value, filterByCreatedBefore)
                      ? "From date cannot be after To date"
                      : true;
                  },
                },
              }}
            />
            <FormDateField
              name="filterByCreatedBefore"
              label="To"
              placeholder="Select a date"
              maxDate={maxDate}
              rules={{
                deps: ["filterByCreatedAfter"],
                validate: {
                  isDateValid,
                  isInFuture: (value) => isInFuture(value, maxDate),
                  isInRange: (value, { filterByCreatedAfter }) => {
                    return value && isBefore(value, filterByCreatedAfter)
                      ? "To date cannot be before From date"
                      : true;
                  },
                },
              }}
            />
          </FlexWrapper>

          <FormSubHeader className={styles.subHeader}>Due Date</FormSubHeader>
          <FlexWrapper className={styles.flexWrapper}>
            <FormDateField
              name="filterByDueDateAfter"
              label="From"
              placeholder="Select a date"
              rules={{
                deps: ["filterByDueDateBefore"],
                validate: {
                  isDateValid,
                  isInRange: (value, { filterByDueDateBefore }) => {
                    return value && isAfter(value, filterByDueDateBefore)
                      ? "From date cannot be after To date"
                      : true;
                  },
                },
              }}
            />
            <FormDateField
              name="filterByDueDateBefore"
              label="To"
              placeholder="Select a date"
              rules={{
                deps: ["filterByDueDateAfter"],
                validate: {
                  isDateValid,
                  isInRange: (value, { filterByDueDateAfter }) => {
                    return value && isBefore(value, filterByDueDateAfter)
                      ? "To date cannot be before From date"
                      : true;
                  },
                },
              }}
            />
          </FlexWrapper>

          <FormSubHeader className={styles.subHeader}>
            Date Updated
          </FormSubHeader>
          <FlexWrapper className={styles.flexWrapper}>
            <FormDateField
              name="filterByUpdatedAfter"
              label="From"
              placeholder="Select a date"
              maxDate={maxDate}
              rules={{
                deps: ["filterByUpdatedBefore"],
                validate: {
                  isDateValid,
                  isInFuture: (value) => isInFuture(value, maxDate),
                  isInRange: (value, { filterByUpdatedBefore }) => {
                    return value && isAfter(value, filterByUpdatedBefore)
                      ? "From date cannot be after To date"
                      : true;
                  },
                },
              }}
            />
            <FormDateField
              name="filterByUpdatedBefore"
              label="To"
              placeholder="Select a date"
              maxDate={maxDate}
              rules={{
                deps: ["filterByUpdatedAfter"],
                validate: {
                  isDateValid,
                  isInFuture: (value) => isInFuture(value, maxDate),
                  isInRange: (value, { filterByUpdatedAfter }) => {
                    return value && isBefore(value, filterByUpdatedAfter)
                      ? "To date cannot be before From date"
                      : true;
                  },
                },
              }}
            />
          </FlexWrapper>

          <Stack
            className={styles.footer}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography className={styles.resetText} onClick={handleFormReset}>
              Reset to default
            </Typography>

            <Stack direction="row" gap="20px">
              <Button
                variant="outlined"
                buttonType="primary"
                text="Cancel"
                onClick={onClose}
              />
              <Button
                variant="contained"
                buttonType="primary"
                text="Apply"
                onClick={handleFormSubmit}
              />
            </Stack>
          </Stack>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default TasksFilterForm;
